import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, MenuItem,
  Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { withSerialNo } from '../../components/ui/gridColumns';
import api from '../../services/api';
import { listTenants } from '../../services/tenantService';
import { selectIsSuperAdmin } from '../../store/slices/authSlice';
import { formatMoney } from '../../utils/currency';

type PopulatedUser = {
  _id?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
};

type PopulatedTenant = {
  _id?: string;
  companyName?: string;
  slug?: string;
};

export type TransactionRow = {
  _id: string;
  tenantId?: string | PopulatedTenant;
  userId?: string | PopulatedUser;
  gateway?: string;
  gatewayOrderId?: string;
  gatewayPaymentId?: string;
  amount?: number;
  currency?: string;
  status?: string;
  referenceType?: string;
  referenceId?: string;
  referenceNumber?: string;
  sandbox?: boolean;
  metadata?: Record<string, unknown>;
  paidAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

const STATUSES = [
  { value: 'all', label: 'All statuses' },
  { value: 'hold', label: 'Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'cancelled', label: 'Cancelled' },
];

const GATEWAYS = [
  { value: 'all', label: 'All gateways' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'razorpay', label: 'Razorpay' },
];

const REFERENCES = [
  { value: 'all', label: 'All references' },
  { value: 'Ride', label: 'Ride' },
  { value: 'FoodOrder', label: 'Food order' },
  { value: 'GroceryOrder', label: 'Grocery order' },
  { value: 'Booking', label: 'Booking' },
  { value: 'wallet_topup', label: 'Wallet top-up' },
];

const MODES = [
  { value: 'all', label: 'Live and sandbox' },
  { value: 'live', label: 'Live' },
  { value: 'sandbox', label: 'Sandbox' },
];

function personName(user?: string | PopulatedUser) {
  if (!user || typeof user === 'string') return '—';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.phone || user.email || '—';
}

function tenantName(tenant?: string | PopulatedTenant) {
  if (!tenant || typeof tenant === 'string') return '—';
  return tenant.companyName || tenant.slug || '—';
}

function statusLabel(status?: string) {
  if (status === 'authorized') return 'Hold';
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function statusColor(status?: string): 'success' | 'warning' | 'error' | 'default' | 'info' {
  if (status === 'completed') return 'success';
  if (status === 'authorized') return 'warning';
  if (status === 'failed' || status === 'cancelled') return 'error';
  if (status === 'refunded') return 'info';
  return 'default';
}

function money(row: TransactionRow) {
  return formatMoney(Number(row.amount || 0), row.currency || 'INR');
}

export default function TenantTransactionsPage() {
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [gateway, setGateway] = useState('all');
  const [referenceType, setReferenceType] = useState('all');
  const [mode, setMode] = useState('all');
  const [tenantId, setTenantId] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<TransactionRow | null>(null);

  const { data: tenants } = useQuery({
    queryKey: ['tenants-for-transactions'],
    queryFn: () => listTenants({ limit: 200, page: 1 }),
    enabled: isSuperAdmin,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['tenant-transactions', page, pageSize, search, status, gateway, referenceType, mode, tenantId, from, to],
    queryFn: async () => {
      const res = await api.get('/payments/admin/transactions', {
        params: {
          page: page + 1,
          limit: pageSize,
          search: search.trim() || undefined,
          status: status === 'all' ? undefined : status,
          gateway: gateway === 'all' ? undefined : gateway,
          referenceType: referenceType === 'all' ? undefined : referenceType,
          mode: mode === 'all' ? undefined : mode,
          tenantId: isSuperAdmin && tenantId !== 'all' ? tenantId : undefined,
          from: from || undefined,
          to: to || undefined,
        },
      });
      const meta = (res.data.meta || {}) as {
        total?: number;
        hold?: number;
        completed?: number;
      };
      return {
        rows: (res.data.data || []) as TransactionRow[],
        total: Number(meta.total || 0),
        hold: Number(meta.hold || 0),
        completed: Number(meta.completed || 0),
      };
    },
  });

  const columns: GridColDef<TransactionRow>[] = useMemo(() => [
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 170,
      valueFormatter: (value) => (value ? new Date(String(value)).toLocaleString() : '—'),
    },
    {
      field: 'tenantId',
      headerName: 'Tenant',
      width: 160,
      valueGetter: (_value, row) => tenantName(row.tenantId),
    },
    {
      field: 'userId',
      headerName: 'Customer',
      width: 160,
      valueGetter: (_value, row) => personName(row.userId),
    },
    { field: 'referenceNumber', headerName: 'Reference', width: 150 },
    { field: 'referenceType', headerName: 'Type', width: 120 },
    {
      field: 'gateway',
      headerName: 'Gateway',
      width: 110,
      valueFormatter: (value) => (value ? String(value) : '—'),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      valueGetter: (_value, row) => money(row),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip size="small" color={statusColor(params.row.status)} label={statusLabel(params.row.status)} />
      ),
    },
    {
      field: 'sandbox',
      headerName: 'Mode',
      width: 110,
      valueGetter: (_value, row) => (row.sandbox ? 'Sandbox' : 'Live'),
    },
  ], []);

  const resetPage = () => setPage(0);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance & Reports"
        title="Transaction history"
        subtitle="Completed charges and card holds. Open a row to see the full transaction."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Transaction history' },
        ]}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip color="warning" label={`Hold ${data?.hold ?? 0}`} />
        <Chip color="success" label={`Completed ${data?.completed ?? 0}`} />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Search"
          placeholder="Reference, payment id, customer id"
          value={search}
          onChange={(e) => { setSearch(e.target.value); resetPage(); }}
        />
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); resetPage(); }}>
          {STATUSES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Gateway" value={gateway} onChange={(e) => { setGateway(e.target.value); resetPage(); }}>
          {GATEWAYS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Reference" value={referenceType} onChange={(e) => { setReferenceType(e.target.value); resetPage(); }}>
          {REFERENCES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Mode" value={mode} onChange={(e) => { setMode(e.target.value); resetPage(); }}>
          {MODES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>
        {isSuperAdmin && (
          <TextField select size="small" label="Tenant" value={tenantId} onChange={(e) => { setTenantId(e.target.value); resetPage(); }}>
            <MenuItem value="all">All tenants</MenuItem>
            {(tenants?.data || []).map((tenant) => (
              <MenuItem key={tenant._id} value={tenant._id}>{tenant.companyName}</MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          size="small"
          label="From"
          type="date"
          value={from}
          onChange={(e) => { setFrom(e.target.value); resetPage(); }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={to}
          onChange={(e) => { setTo(e.target.value); resetPage(); }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <DataGrid
        rows={data?.rows || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(row) => row._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        pageSizeOptions={[10, 20, 50]}
        onRowClick={(params) => setSelected(params.row)}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, cursor: 'pointer' }}
      />

      <TransactionDetail open={!!selected} row={selected} onClose={() => setSelected(null)} />
    </PageShell>
  );
}

function TransactionDetail({
  open,
  row,
  onClose,
}: {
  open: boolean;
  row: TransactionRow | null;
  onClose: () => void;
}) {
  const user = row && typeof row.userId === 'object' ? row.userId : undefined;
  const tenant = row && typeof row.tenantId === 'object' ? row.tenantId : undefined;
  const meta = row?.metadata && Object.keys(row.metadata).length ? row.metadata : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Transaction
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {row && (
          <Stack spacing={1.25}>
            <Detail label="Status" value={statusLabel(row.status)} />
            <Detail label="Amount" value={money(row)} />
            <Detail label="Gateway" value={row.gateway || '—'} />
            <Detail label="Mode" value={row.sandbox ? 'Sandbox' : 'Live'} />
            <Detail label="Tenant" value={tenant?.companyName || tenant?.slug || '—'} />
            <Detail label="Tenant id" value={tenant?._id || (typeof row.tenantId === 'string' ? row.tenantId : '—')} />
            <Detail label="Customer" value={personName(row.userId)} />
            <Detail label="Phone" value={user?.phone || '—'} />
            <Detail label="Email" value={user?.email || '—'} />
            <Detail label="Customer id" value={user?._id || (typeof row.userId === 'string' ? row.userId : '—')} />
            <Detail label="Reference" value={row.referenceNumber || '—'} />
            <Detail label="Reference type" value={row.referenceType || '—'} />
            <Detail label="Reference id" value={row.referenceId || '—'} />
            <Detail label="Transaction id" value={row._id} />
            <Detail label="Gateway order id" value={row.gatewayOrderId || '—'} />
            <Detail label="Gateway payment id" value={row.gatewayPaymentId || '—'} />
            <Detail label="Created" value={row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'} />
            <Detail label="Updated" value={row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'} />
            <Detail label="Paid at" value={row.paidAt ? new Date(row.paidAt).toLocaleString() : '—'} />
            {meta && (
              <Box>
                <Typography variant="caption" color="text.secondary">Metadata</Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(meta, null, 2)}
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</Typography>
    </Stack>
  );
}
